import type {RefObject} from "react";
import {MathUtils} from "../../../utils/math/MathUtils";

// =============================================================================
// Constants & persisted layout shape
// =============================================================================

/** Width in pixels of the thin drag column between two panes. */
export const SPLITTER_V5_DRAG_HANDLE_WIDTH_PX = 4;

/** Legacy `localStorage` key for docked catalog + main (xyicon list). New data uses SPLITTER_V5_LOCAL_STORAGE_KEY_SPACE_EDITOR_DOCK_MAIN. */
export const SPLITTER_V5_LOCAL_STORAGE_KEY_CATALOG_MAIN = "v5-splitter-catalog-docked-main";

/**
 * Xyicon list (catalog docked) and space editor (any docked tool + main, no side grids): one saved ratio so list ↔ space
 * match and switching catalog / boundary / unplotted does not jump.
 */
export const SPLITTER_V5_LOCAL_STORAGE_KEY_SPACE_EDITOR_DOCK_MAIN = "v5-splitter-space-editor-dock-main";

/** Main pane min width (px) for module `MainPanelStyled` / space `SpaceEditorStyled` in the dock+main splitter layout. */
export const SPLITTER_V5_CATALOG_MAIN_SECOND_PANE_MIN_WIDTH_PX = 500;

export const RATIO_EPSILON = 1e-9;

/**
 * Three different "small" thresholds — same family of idea, different jobs (do not merge into one number):
 *
 * 1) SPLITTER_WEIGHT_SUM_EPSILON (1e-12) — **Validity of stored or input totals.** After adding many non-negative
 *    weights, is the total big enough to mean “there is a real layout”? Rejects empty/corrupt data and near-zero sums.
 *
 * 2) SPLITTER_COLLAPSED_RATIO_SUM_EPSILON (1e-14) — **Safety inside one iterative loop.** In constrainUnitRatiosToPanelBounds
 *    we clamp each ratio then renormalize by dividing by the sum. If that sum underflows toward zero, dividing is
 *    pointless or unstable — stop. Slightly stricter than (1) so we detect collapse *after* intermediate steps, not
 *    only when reading JSON.
 *
 * 3) SPLITTER_FIRST_SHARE_CLAMP_EPSILON (1e-8) — **Product rule, not tolerance.** When inserting a new first column,
 *    its share must stay strictly between 0 and 1 so remaining panes always get a positive slice. That needs a
 *    **visible** margin (1e-8), not a float-dust margin (1e-12): same order of idea as “don’t use 100% or 0% for one pane.”
 */
export const SPLITTER_WEIGHT_SUM_EPSILON = 1e-12;
const SPLITTER_COLLAPSED_RATIO_SUM_EPSILON = 1e-14;
const SPLITTER_FIRST_SHARE_CLAMP_EPSILON = 1e-8;

/** Saved shares should sum to about 1; allow this much drift above 1 (rounding, JSON). */
const PERSISTED_UNIT_SUM_UPPER = 1.001;

/** Slightly looser upper bound when validating fallback arrays in resolvePersistedRatios. */
const PERSISTED_UNIT_SUM_UPPER_LOOSE = PERSISTED_UNIT_SUM_UPPER + 1e-6;

export type SplitterPersistedLayoutV5 = {
	readonly current: number[];
	readonly lastTwoPaneRatios?: readonly [number, number];
	readonly lastByPaneCount?: Readonly<Partial<Record<number, readonly number[]>>>;
};

// =============================================================================
// Splitter content width (full width minus drag-handle tracks)
// =============================================================================

/** Full splitter width minus the drag-handle columns, so we know how wide panes can be together. */
export function getSplitterContentWidthPx(splitterRef: RefObject<HTMLDivElement | null>, paneCount: number): number {
	const fullWidthPx = splitterRef.current?.offsetWidth ?? 0;
	const dragHandlesPx = Math.max(0, paneCount - 1) * SPLITTER_V5_DRAG_HANDLE_WIDTH_PX;

	return Math.max(1, fullWidthPx - dragHandlesPx);
}

/** Same idea as getSplitterContentWidthPx: width left for panes after subtracting drag-handle pixels. */
export function calculateMainPanelWidth(splitterRef: RefObject<HTMLDivElement | null>, paneCount: number): number {
	const fullWidthPx = splitterRef.current?.offsetWidth ?? 0;
	const dragHandlesPx = Math.max(0, paneCount - 1) * SPLITTER_V5_DRAG_HANDLE_WIDTH_PX;

	return fullWidthPx - dragHandlesPx;
}

/** Adds up default or min widths from `data-default-width` or `data-min-width` on pane elements. */
export function sumDomDataWidths(children: Element[], type: "default" | "min"): number {
	let totalWidthPx = 0;

	for (const child of children) {
		const widthAttrValue = parseInt(child.getAttribute(`data-${type}-width`));
		const widthPx = isNaN(widthAttrValue) ? 0 : widthAttrValue;
		totalWidthPx += widthPx;
	}

	return totalWidthPx;
}

// =============================================================================
// Recalculate layout: DOM metrics → initial pane ratios (useSplitterV5Layout)
// =============================================================================

/** Per-pane result from reading `data-*` widths during a full `recalculateLayout` pass. */
export type SplitterRecalculatePaneSizingRow = {
	readonly minWidth: number;
	readonly maxWidth: number;
	readonly finalWidthRatio: number;
	/** Carries `widthOffset` across panes in the same loop (matches the hook’s mutable `widthOffset`). */
	readonly nextWidthOffset: number;
};

/**
 * Whether the splitter grid’s immediate children match the expected pane/handle pattern, and stride for indexing
 * pane roots among those children (`1` vs `2` when handles are interleaved).
 */
export function getSplitterDomTopologyAndStride(
	validPaneCount: number,
	domChildCount: number,
): {readonly domTopologyMatches: boolean; readonly stride: number} {
	const expectedWithSplitterDragHandles = validPaneCount * 2 - 1;
	const expectedDomChildCount = validPaneCount <= 1 ? validPaneCount : validPaneCount * 2 - 1;
	const domTopologyMatches = domChildCount === expectedDomChildCount;
	const stride = domChildCount >= expectedWithSplitterDragHandles && validPaneCount > 1 ? 2 : 1;

	return {domTopologyMatches, stride};
}

/**
 * One pane’s initial width ratio and min/max from DOM (or cached metrics) during `recalculateLayout`.
 * Must stay identical to the former in-hook branch in `useSplitterV5Layout` (mutable `widthOffset` → `nextWidthOffset`).
 */
export function computeRecalculatePaneSizingRow(params: {
	readonly validPaneCount: number;
	readonly domTopologyMatches: boolean;
	readonly cachedMetrics: {minWidth: number; maxWidth: number} | undefined;
	readonly domChildMaybe: Element | undefined;
	readonly splitterContentWidthPx: number;
	readonly sumOfDefaultWidths: number;
	readonly sumOfMinWidths: number;
	readonly widthOffset: number;
}): SplitterRecalculatePaneSizingRow {
	const {validPaneCount, domTopologyMatches, cachedMetrics, domChildMaybe, splitterContentWidthPx, sumOfDefaultWidths, sumOfMinWidths, widthOffset} =
		params;

	if (validPaneCount <= 1) {
		return {minWidth: 0, maxWidth: 0, finalWidthRatio: 1, nextWidthOffset: widthOffset};
	}

	if (!domTopologyMatches && cachedMetrics) {
		return {
			minWidth: cachedMetrics.minWidth,
			maxWidth: cachedMetrics.maxWidth,
			finalWidthRatio: 1 / validPaneCount,
			nextWidthOffset: widthOffset,
		};
	}

	const uniformShare = 1 / validPaneCount;
	const minWidthMaybe = parseInt(domChildMaybe?.getAttribute("data-min-width") ?? "");
	const minWidth = isNaN(minWidthMaybe) ? 0 : minWidthMaybe;
	const minWidthRatio = minWidth / splitterContentWidthPx;
	const maxWidthMaybe = parseInt(domChildMaybe?.getAttribute("data-max-width") ?? "");
	const maxWidth = isNaN(maxWidthMaybe) ? 0 : maxWidthMaybe;

	const defaultWidthMaybe = parseInt(domChildMaybe?.getAttribute("data-default-width") ?? "");

	let defaultWidthRatio = MathUtils.isValidNumber(defaultWidthMaybe) ? defaultWidthMaybe / splitterContentWidthPx : uniformShare;

	let nextWidthOffset: number;

	if (MathUtils.isValidNumber(defaultWidthMaybe)) {
		nextWidthOffset = widthOffset + defaultWidthMaybe / splitterContentWidthPx - uniformShare;
	} else {
		defaultWidthRatio -= widthOffset;
		nextWidthOffset = 0;
	}

	let finalWidthRatio = defaultWidthRatio;

	if (sumOfDefaultWidths > splitterContentWidthPx) {
		finalWidthRatio = minWidthRatio;
	}

	if (sumOfMinWidths > splitterContentWidthPx) {
		finalWidthRatio = uniformShare;
	}

	return {minWidth, maxWidth, finalWidthRatio, nextWidthOffset};
}

/**
 * Derives `newFirstPanelFraction` for `resolvePersistedRatios` (`insertFirstPanelShare` path). Matches the hook block 1:1.
 */
export function computeNewFirstPanelFractionForRecalculate(params: {
	readonly validPaneCount: number;
	readonly splitterContentWidthPx: number;
	readonly firstPaneMinWidth: number;
	readonly firstPaneMaxWidth: number;
	readonly lastByPaneCountForN: readonly number[] | undefined;
	readonly domTopologyMatches: boolean;
	readonly firstDomChildMaybe: Element | undefined;
}): number | undefined {
	const {validPaneCount, splitterContentWidthPx, firstPaneMinWidth, firstPaneMaxWidth, lastByPaneCountForN, domTopologyMatches, firstDomChildMaybe} =
		params;

	const n = validPaneCount;
	if (n < 2 || splitterContentWidthPx <= 0) {
		return undefined;
	}

	const minF = firstPaneMinWidth / splitterContentWidthPx;
	const maxF = firstPaneMaxWidth > 0 ? Math.min(firstPaneMaxWidth / splitterContentWidthPx, 1) : 1;

	const snap = lastByPaneCountForN;
	let firstShareFromSnap: number | undefined;

	if (snap && snap.length === n) {
		const sumSnap = snap.reduce((a, b) => a + b, 0);

		if (sumSnap > 1e-12 && snap.every((v) => MathUtils.isValidNumber(v) && v >= 0)) {
			firstShareFromSnap = snap[0] / sumSnap;
		}
	}
	if (firstShareFromSnap === undefined) {
		const defaultWidthPx = parseInt(firstDomChildMaybe?.getAttribute("data-default-width") ?? "");

		if (MathUtils.isValidNumber(defaultWidthPx)) {
			firstShareFromSnap = defaultWidthPx / splitterContentWidthPx;
		}
	}

	if (firstShareFromSnap === undefined) {
		return undefined;
	}

	let newFirstPanelFraction = Math.min(Math.max(firstShareFromSnap, minF), maxF);

	if (newFirstPanelFraction <= 1e-8 || newFirstPanelFraction >= 1 - 1e-8) {
		return undefined;
	}
	return newFirstPanelFraction;
}

// =============================================================================
// Browser storage: parse / stringify
// =============================================================================

/** Reads saved splitter data from a string (older saves may be a plain number array). */
export function parseSplitterLayoutV5(raw: string | null): SplitterPersistedLayoutV5 {
	if (!raw) {
		return {current: []};
	}

	try {
		const parsed = JSON.parse(raw) as unknown;

		if (Array.isArray(parsed)) {
			const arr = parsed as number[];
			const lastTwo = arr.length === 2 && areValidRatioEntries(arr) ? ([arr[0], arr[1]] as [number, number]) : undefined;

			return {current: arr, lastTwoPaneRatios: lastTwo};
		}

		if (parsed && typeof parsed === "object") {
			const o = parsed as {current?: unknown; lastTwoPaneRatios?: unknown; lastByPaneCount?: unknown};
			const current = Array.isArray(o.current) ? (o.current as number[]) : [];

			let lastTwo: [number, number] | undefined;

			if (Array.isArray(o.lastTwoPaneRatios) && o.lastTwoPaneRatios.length === 2 && areValidRatioEntries(o.lastTwoPaneRatios as number[])) {
				const [a, b] = o.lastTwoPaneRatios as number[];
				lastTwo = [a, b];
			}

			const lastByPaneCount = parseLastByPaneCount(o.lastByPaneCount);

			return {current, lastTwoPaneRatios: lastTwo, lastByPaneCount};
		}
	} catch {
		console.error("Failed to parse splitter layout", raw);
	}

	return {current: []};
}

/** Turns the saved layout object into a string for browser storage. */
export function stringifySplitterLayoutV5(layout: SplitterPersistedLayoutV5): string {
	const payload: Record<string, unknown> = {current: layout.current};

	if (layout.lastTwoPaneRatios && layout.lastTwoPaneRatios.length === 2) {
		payload.lastTwoPaneRatios = [...layout.lastTwoPaneRatios];
	}

	if (layout.lastByPaneCount && Object.keys(layout.lastByPaneCount).length > 0) {
		const by: Record<string, number[]> = {};

		for (const [k, v] of Object.entries(layout.lastByPaneCount)) {
			const n = parseInt(k, 10);

			if (!isNaN(n) && Array.isArray(v) && v.length === n) {
				by[String(n)] = [...v];
			}
		}

		if (Object.keys(by).length > 0) {
			payload.lastByPaneCount = by;
		}
	}

	return JSON.stringify(payload);
}

/** If the shared dock key has no usable layout but the legacy catalog key does, copy so saved list widths are preserved. */
export function ensureSpaceEditorDockMainSeededFromLegacyCatalogKey(): void {
	try {
		const sharedRaw = localStorage.getItem(SPLITTER_V5_LOCAL_STORAGE_KEY_SPACE_EDITOR_DOCK_MAIN);
		const sharedLayout = parseSplitterLayoutV5(sharedRaw);

		const hasSharedSaved =
			sharedLayout.current.length > 0 ||
			(sharedLayout.lastByPaneCount != null && Object.keys(sharedLayout.lastByPaneCount).length > 0) ||
			sharedLayout.lastTwoPaneRatios != null;

		if (hasSharedSaved) {
			return;
		}
		const legacy = localStorage.getItem(SPLITTER_V5_LOCAL_STORAGE_KEY_CATALOG_MAIN);

		if (legacy != null && legacy !== "") {
			localStorage.setItem(SPLITTER_V5_LOCAL_STORAGE_KEY_SPACE_EDITOR_DOCK_MAIN, legacy);
		}
	} catch {
		console.error("Failed to ensure space editor dock main seeded from legacy catalog key");
	}
}

/** Reads the saved "widths per number of panes" map if the stored data looks valid. */
function parseLastByPaneCount(raw: unknown): Partial<Record<number, readonly number[]>> | undefined {
	if (!raw || typeof raw !== "object") {
		return undefined;
	}

	const parsedByPaneCount: Partial<Record<number, readonly number[]>> = {};

	for (const [key, val] of Object.entries(raw as Record<string, unknown>)) {
		const n = parseInt(key, 10);
		if (isNaN(n) || n < 1 || !Array.isArray(val) || val.length !== n) {
			continue;
		}

		const arr = val as number[];
		if (!areValidRatioEntries(arr)) {
			continue;
		}

		parsedByPaneCount[n] = arr.slice();
	}

	return Object.keys(parsedByPaneCount).length > 0 ? parsedByPaneCount : undefined;
}

// =============================================================================
// Ratio validation & normalization
// =============================================================================

function areValidRatioEntries(arr: readonly number[]): boolean {
	return arr.every((v) => MathUtils.isValidNumber(v) && v >= 0);
}

/** True if the saved width list is usable: not empty, not negative, and adds up to about 1. */
export function isUsablePersistedCurrent(arr: number[]): boolean {
	if (!Array.isArray(arr) || arr.length === 0) {
		return false;
	}
	if (!areValidRatioEntries(arr)) {
		return false;
	}

	const sum = MathUtils.sum(arr);

	return sum > SPLITTER_WEIGHT_SUM_EPSILON && sum <= PERSISTED_UNIT_SUM_UPPER;
}

/** Scales numbers so they add up to 1 (each value is a share of the whole row). */
export function normalizeToUnitSum(weights: readonly number[]): number[] {
	const totalWeight = weights.reduce((sum, value) => sum + value, 0) || 1;

	return [...weights].map((weight) => weight / totalWeight);
}

/** When the first pane goes away, spreads its share across the others in proportion to their sizes. */
export function redistributeAfterRemovingFirstPanel(frac: readonly number[]): number[] {
	if (frac.length < 2) {
		return [...frac];
	}

	const removedFirstPaneWeight = frac[0];
	const remainingPaneWeights = frac.slice(1);
	const remainingWeightSum = remainingPaneWeights.reduce((sum, value) => sum + value, 0);

	if (remainingWeightSum < SPLITTER_WEIGHT_SUM_EPSILON) {
		const uniformRemainingWeight = 1 / remainingPaneWeights.length;

		return remainingPaneWeights.map(() => uniformRemainingWeight);
	}

	return remainingPaneWeights.map((weight) => weight + removedFirstPaneWeight * (weight / remainingWeightSum));
}

/** Adds a new first pane with a chosen share and shrinks the others to fill the rest of the row. */
export function insertFirstPanelShare(restUnitNormalized: readonly number[], firstShare: number): number[] {
	const clampedFirstShare = Math.min(Math.max(firstShare, SPLITTER_FIRST_SHARE_CLAMP_EPSILON), 1 - SPLITTER_FIRST_SHARE_CLAMP_EPSILON);
	const remainingShare = 1 - clampedFirstShare;
	const remainingNormalizedSum = restUnitNormalized.reduce((sum, value) => sum + value, 0) || 1;
	const scaledRemainingShares = restUnitNormalized.map((weight) => (weight / remainingNormalizedSum) * remainingShare);

	return [clampedFirstShare, ...scaledRemainingShares];
}

export type ResolvePersistedRatiosOptions = {
	readonly newFirstPanelFraction?: number;
	readonly allowRemoveFirstProjection?: boolean;
};

/**
 * Picks starting pane shares from saved data (current layout, older snapshots, two-pane shortcut), or returns null to use defaults from the pane elements on the page.
 */
export function resolvePersistedRatios(
	paneCount: number,
	layout: SplitterPersistedLayoutV5,
	options?: ResolvePersistedRatiosOptions,
): number[] | null {
	if (paneCount < 1) {
		return null;
	}

	const {current, lastTwoPaneRatios, lastByPaneCount} = layout;

	// Exact match on current saved row
	if (current.length === paneCount && isUsablePersistedCurrent(current)) {
		return normalizeToUnitSum(current);
	}

	// One fewer pane: project by dropping first column
	if (options?.allowRemoveFirstProjection && current.length === paneCount + 1 && current.length >= 2 && areValidRatioEntries(current)) {
		const sum = MathUtils.sum(current);

		if (sum > SPLITTER_WEIGHT_SUM_EPSILON && sum <= PERSISTED_UNIT_SUM_UPPER_LOOSE) {
			const projected = redistributeAfterRemovingFirstPanel(normalizeToUnitSum(current));
			if (projected.length === paneCount) {
				return normalizeToUnitSum(projected);
			}
		}
	}

	// Snapshot for this pane count
	const snap = lastByPaneCount?.[paneCount];

	if (snap && snap.length === paneCount && areValidRatioEntries(snap)) {
		const sum = MathUtils.sum([...snap]);

		if (sum > SPLITTER_WEIGHT_SUM_EPSILON && sum <= PERSISTED_UNIT_SUM_UPPER_LOOSE) {
			return normalizeToUnitSum(snap);
		}
	}

	// Last saved two-pane drag — before insert-from-defaults. List→docked keeps `current` as [1]; insert would use
	// data-default-width (~440px) and flash until a later path would apply storage.
	if (paneCount === 2 && lastTwoPaneRatios && areValidRatioEntries(lastTwoPaneRatios)) {
		const sum = lastTwoPaneRatios[0] + lastTwoPaneRatios[1];

		if (sum > SPLITTER_WEIGHT_SUM_EPSILON) {
			return normalizeToUnitSum(lastTwoPaneRatios);
		}
	}

	// One more pane: insert first column (DOM default or lastByPaneCount-derived fraction when no lastTwo)
	if (
		options?.newFirstPanelFraction !== undefined &&
		current.length === paneCount - 1 &&
		paneCount >= 2 &&
		current.length >= 1 &&
		areValidRatioEntries(current)
	) {
		const sum = MathUtils.sum(current);

		if (sum > SPLITTER_WEIGHT_SUM_EPSILON) {
			const inserted = insertFirstPanelShare(normalizeToUnitSum(current), options.newFirstPanelFraction);
			if (inserted.length === paneCount) {
				return normalizeToUnitSum(inserted);
			}
		}
	}

	return null;
}

// =============================================================================
// Min/max as fractions of row width
// =============================================================================

/** Keeps each pane's share inside min/max pixel widths, then scales shares so they still add up to 1. */
export function constrainUnitRatiosToPanelBounds(
	ratios: readonly number[],
	minPx: readonly number[],
	maxPx: readonly number[],
	contentWidthPx: number,
): number[] {
	const paneCount = ratios.length;

	if (paneCount === 0 || contentWidthPx < 1) {
		return [...ratios];
	}

	if (minPx.length !== paneCount || maxPx.length !== paneCount) {
		return normalizeToUnitSum(ratios);
	}

	const normalizedRatios = normalizeToUnitSum(ratios);
	const minRatios = minPx.map((minWidthPx) => Math.min(Math.max(minWidthPx / contentWidthPx, 0), 1));
	const maxRatios = maxPx.map((maxWidthPx) => (maxWidthPx > 0 ? Math.min(maxWidthPx / contentWidthPx, 1) : 1));

	const minRatioSum = minRatios.reduce((sum, value) => sum + value, 0);

	if (minRatioSum > 1 + RATIO_EPSILON) {
		return normalizeToUnitSum(minRatios);
	}

	const maxRatioSum = maxRatios.reduce((sum, value) => sum + value, 0);

	if (maxRatioSum < 1 - RATIO_EPSILON) {
		return normalizeToUnitSum(maxRatios);
	}

	for (let iter = 0; iter < 80; iter++) {
		let changed = false;

		for (let i = 0; i < paneCount; i++) {
			const clampedRatio = Math.min(Math.max(normalizedRatios[i], minRatios[i]), maxRatios[i]);

			if (Math.abs(clampedRatio - normalizedRatios[i]) > RATIO_EPSILON) {
				changed = true;
			}

			normalizedRatios[i] = clampedRatio;
		}

		const ratioSum = normalizedRatios.reduce((sum, value) => sum + value, 0);

		if (ratioSum < SPLITTER_COLLAPSED_RATIO_SUM_EPSILON) {
			break;
		}

		if (Math.abs(ratioSum - 1) < RATIO_EPSILON && !changed) {
			break;
		}

		for (let i = 0; i < paneCount; i++) {
			normalizedRatios[i] /= ratioSum;
		}
	}

	return normalizedRatios;
}

// =============================================================================
// Drag: move share between neighbors, chain when min/max bind
// =============================================================================

/**
 * Moves width between two neighboring panes for one drag step. If a pane cannot move further because of min/max,
 * the extra movement is passed along to other panes. Changes the `paneRatiosInOut` array directly (same array object).
 */
export function applyDragWithChainPropagation(
	paneRatiosInOut: number[],
	minPx: number[],
	maxPx: number[],
	contentWidthPx: number,
	leftPaneIndex: number,
	rightPaneIndex: number,
	requestedDeltaRatio: number,
): void {
	const paneCount = paneRatiosInOut.length;

	if (paneCount === 0 || contentWidthPx < 1 || rightPaneIndex !== leftPaneIndex + 1) {
		return;
	}

	// Normalize incoming weights to sum to 1
	let initialRatioSum = 0;

	for (let i = 0; i < paneCount; i++) {
		initialRatioSum += paneRatiosInOut[i];
	}

	if (initialRatioSum <= SPLITTER_WEIGHT_SUM_EPSILON) {
		initialRatioSum = 1;

		for (let i = 0; i < paneCount; i++) {
			paneRatiosInOut[i] = 1 / paneCount;
		}
	} else {
		for (let i = 0; i < paneCount; i++) {
			paneRatiosInOut[i] /= initialRatioSum;
		}
	}

	const minRatios = minPx.map((minWidthPx) => Math.min(Math.max(minWidthPx / contentWidthPx, 0), 1));
	const maxRatios = maxPx.map((maxWidthPx) => (maxWidthPx > 0 ? Math.min(maxWidthPx / contentWidthPx, 1) : 1));

	paneRatiosInOut[leftPaneIndex] += requestedDeltaRatio;
	paneRatiosInOut[rightPaneIndex] -= requestedDeltaRatio;

	const fixUnders = (): boolean => {
		let changed = false;

		for (let paneIndex = leftPaneIndex + 1; paneIndex < paneCount; paneIndex++) {
			if (paneRatiosInOut[paneIndex] < minRatios[paneIndex] - RATIO_EPSILON) {
				const neededRatio = minRatios[paneIndex] - paneRatiosInOut[paneIndex];
				paneRatiosInOut[paneIndex] = minRatios[paneIndex];

				if (paneIndex + 1 < paneCount) {
					paneRatiosInOut[paneIndex + 1] -= neededRatio;
				} else {
					paneRatiosInOut[leftPaneIndex] -= neededRatio;
				}
				changed = true;
			}
		}

		for (let paneIndex = leftPaneIndex; paneIndex >= 0; paneIndex--) {
			if (paneRatiosInOut[paneIndex] < minRatios[paneIndex] - RATIO_EPSILON) {
				const neededRatio = minRatios[paneIndex] - paneRatiosInOut[paneIndex];
				paneRatiosInOut[paneIndex] = minRatios[paneIndex];

				if (paneIndex - 1 >= 0) {
					paneRatiosInOut[paneIndex - 1] -= neededRatio;
				} else {
					paneRatiosInOut[rightPaneIndex] -= neededRatio;
				}
				changed = true;
			}
		}

		return changed;
	};

	const fixOvers = (): boolean => {
		let changed = false;

		for (let paneIndex = 0; paneIndex < paneCount; paneIndex++) {
			if (paneRatiosInOut[paneIndex] > maxRatios[paneIndex] + RATIO_EPSILON) {
				const excessRatio = paneRatiosInOut[paneIndex] - maxRatios[paneIndex];
				paneRatiosInOut[paneIndex] = maxRatios[paneIndex];

				if (paneIndex + 1 < paneCount) {
					paneRatiosInOut[paneIndex + 1] += excessRatio;
				} else if (paneIndex - 1 >= 0) {
					paneRatiosInOut[paneIndex - 1] += excessRatio;
				}
				changed = true;
			}
		}

		return changed;
	};

	for (let iter = 0; iter < 40; iter++) {
		const fixedUnders = fixUnders();
		const fixedOvers = fixOvers();

		if (!fixedUnders && !fixedOvers) {
			break;
		}
	}

	// Scale back to original total weight
	for (let i = 0; i < paneCount; i++) {
		paneRatiosInOut[i] *= initialRatioSum;
	}
}

// =============================================================================
// After pointer-up: normalize rows & merge persisted layout
// =============================================================================

/** Scales pane rows so widths add up to 1 and stores that back on each row for saving. */
export function normalizePanelWidthsForPersist<T extends {width: number; originalRatio?: number}>(rows: T[]): number[] {
	const totalWidth = rows.reduce((sum, row) => sum + (row.width ?? 0), 0) || 1;
	const normalizedRatios: number[] = [];

	for (const row of rows) {
		const normalizedWidth = (row.width ?? 0) / totalWidth;
		row.width = normalizedWidth;
		row.originalRatio = normalizedWidth;
		normalizedRatios.push(normalizedWidth);
	}

	return normalizedRatios;
}

/** Updates the saved layout after a drag: current widths, optional two-pane copy, and last widths for each pane count. */
export function mergePersistedLayoutAfterPointerUp(prev: SplitterPersistedLayoutV5, normalizedCurrent: number[]): SplitterPersistedLayoutV5 {
	let lastTwo = prev.lastTwoPaneRatios;

	if (normalizedCurrent.length === 2) {
		lastTwo = [normalizedCurrent[0], normalizedCurrent[1]] as [number, number];
	}

	const n = normalizedCurrent.length;
	const lastByPaneCount: Partial<Record<number, readonly number[]>> = {...(prev.lastByPaneCount ?? {})};
	lastByPaneCount[n] = [...normalizedCurrent];

	return {current: normalizedCurrent, lastTwoPaneRatios: lastTwo, lastByPaneCount};
}

// =============================================================================
// CSS grid column template for the splitter root only
// =============================================================================

/**
 * Builds the column width string for the splitter element only (pane columns + fixed drag columns).
 * Does not affect layout inside a pane's own content.
 */
export function buildSplitterLayoutGridTemplateColumns(
	childrenState: Array<{splitterLayoutSegment?: string; width?: number; minWidth?: number}>,
	splitterDragHandleWidthPx: number,
): string {
	const splitterPaneStates = childrenState.filter((childState) => childState?.splitterLayoutSegment === "splitterPane");
	const totalSplitterPaneWidthWeight = splitterPaneStates.reduce((sum, childState) => sum + (childState?.width ?? 0), 0);
	const equalSharePercent = splitterPaneStates.length > 0 ? 100 / splitterPaneStates.length : 0;

	return childrenState
		.map((childState) => {
			if (childState?.splitterLayoutSegment !== "splitterPane") {
				return `${splitterDragHandleWidthPx}px`;
			}

			const paneWeight = childState?.width ?? 0;
			const normalizedWidth = totalSplitterPaneWidthWeight > 0 ? paneWeight / totalSplitterPaneWidthWeight : equalSharePercent / 100;
			const minWidthPx = childState?.minWidth ?? 0;
			const flexPercent = normalizedWidth * 100;

			return `minmax(${minWidthPx}px, ${flexPercent}%)`;
		})
		.join(" ");
}
